/**
 * `dropdown` — `DropdownValue` (`specs/M2-tier1-column-types.md` §5.8, `[09§A.7 dropdown]`).
 *
 * Two serialisations again, and they differ in the **key name**: `settings_str` carries
 * `labels: [{id, name}]` while `Column.settings` carries `labels: [{id, label, is_deactivated}]`
 * (recorded side by side in `0213-tier-b-columns-settings-and-settings-str.json`). The canonical
 * store is the `settings_str` form.
 *
 * `value` is `{"ids": [1, 3]}` — option **ids**, resolved through the labels `[cvs§2 dropdown]`.
 * `toText` joins the labels in the **stored id order** (`{"ids":[22,23,24,20]}` renders 22, 23,
 * 24, 20 in that order), and an **empty cell's `text` is `null`, never `""`** (698 null, 0
 * empty) — the opposite of `text` and `date`, and confirmed by the recorded corpus.
 *
 * `create_labels_if_missing` is a service-layer concern: it *"requires permission to change the
 * board structure"* `[09§A.7 dropdown]`.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql, sqlFragment } from '../sql.js';
import { textShadow } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
/** The `settings_str` label shape: `{id, name}`. */
export const dropdownLabelSchema = z.looseObject({
    id: z.number().int(),
    name: z.string(),
});
import { dropdownFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const dropdownSettingsSchema = z.looseObject({
    labels: z.array(dropdownLabelSchema),
    deactivated_labels: z.array(z.number()).optional(),
    /** `create_dropdown_column` input keys `[09§A.2 Columns]`. */
    limit_select: z.boolean().optional(),
    label_limit_count: z.number().int().optional(),
    hide_footer: z.boolean().optional(),
});
export const dropdownValueSchema = z.looseObject({
    ids: z.array(z.number().int()),
});
export const DEFAULT_DROPDOWN_SETTINGS = { labels: [], deactivated_labels: [] };
export function findDropdownLabel(settings, id) {
    return settings.labels.find((label) => label.id === id)?.name;
}
export function findDropdownIdByLabel(settings, text) {
    const exact = settings.labels.find((label) => label.name === text);
    if (exact !== undefined)
        return exact.id;
    const lower = text.toLowerCase();
    return settings.labels.find((label) => label.name.toLowerCase() === lower)?.id;
}
export function isDeactivatedOption(settings, id) {
    return (settings.deactivated_labels ?? []).includes(id);
}
/** `max(id) + 1`, never a reused id (open question Q57). */
export function nextDropdownId(settings) {
    return settings.labels.length === 0 ? 1 : Math.max(...settings.labels.map((l) => l.id)) + 1;
}
function readIds(raw, settings) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'number')
        return [raw];
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed === '')
            return null;
        // `change_simple_column_value`: `"1,2"` or `"Marketing, Engineering"` `[09§A.7]`.
        return trimmed.split(',').map((part) => {
            const token = part.trim();
            if (/^\d+$/.test(token))
                return Number(token);
            const id = findDropdownIdByLabel(settings, token);
            if (id === undefined)
                throw new ColumnValueError('dropdown', `unknown label "${token}"`);
            return id;
        });
    }
    if (Array.isArray(raw))
        return readIds({ ids: raw }, settings);
    if (typeof raw === 'object') {
        const record = raw;
        if (Array.isArray(record.ids)) {
            return record.ids.map((entry) => {
                const id = typeof entry === 'string' && /^\d+$/.test(entry) ? Number(entry) : entry;
                if (typeof id !== 'number' || !Number.isInteger(id)) {
                    throw new ColumnValueError('dropdown', '"ids" must contain integers');
                }
                return id;
            });
        }
        if (Array.isArray(record.labels)) {
            return record.labels.map((entry) => {
                if (typeof entry !== 'string') {
                    throw new ColumnValueError('dropdown', '"labels" must contain strings');
                }
                const id = findDropdownIdByLabel(settings, entry);
                if (id === undefined) {
                    throw new ColumnValueError('dropdown', `unknown label "${entry}" (use create_labels_if_missing at the service layer)`);
                }
                return id;
            });
        }
        return null;
    }
    throw new ColumnValueError('dropdown', `expected an object, received ${typeof raw}`);
}
function dedupe(ids) {
    return [...new Set(ids)];
}
function parseDropdown(raw, settings) {
    const ids = readIds(raw, settings);
    if (ids === null || ids.length === 0)
        return null;
    const unique = dedupe(ids);
    for (const id of unique) {
        if (settings.labels.length > 0 && findDropdownLabel(settings, id) === undefined) {
            throw new ColumnValueError('dropdown', `option id ${id} does not exist in column`);
        }
    }
    const limit = settings.limit_select === true ? (settings.label_limit_count ?? 1) : null;
    if (limit !== null && unique.length > limit) {
        throw new ColumnValueError('dropdown', `at most ${limit} options may be selected`);
    }
    return { ids: unique };
}
/** Labels joined `", "` in the **stored id order**; `null` for an empty cell `[cvs§2]`. */
function renderDropdownText(value, settings) {
    if (value === null)
        return null;
    return value.ids
        .map((id) => findDropdownLabel(settings, id))
        .filter((label) => label !== undefined)
        .join(', ');
}
export const dropdownColumn = defineColumnType({
    type: 'dropdown',
    title: 'Dropdown',
    description: 'One or more options from a fixed list, addressed by option id.',
    apiFragmentName: 'DropdownValue',
    legacyTypeName: 'dropdown',
    idPrefix: 'dropdown',
    valueSchema: dropdownValueSchema,
    settingsSchema: dropdownSettingsSchema,
    defaultSettings: DEFAULT_DROPDOWN_SETTINGS,
    settingsStrSchema: z.looseObject({}),
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    filterConditions: dropdownFilterConditions,
    filterOperators: operatorsOf(dropdownFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    /** The only tier-1 type whose empty `text` is null `[cvs§2 dropdown]`. */
    emptyText: null,
    parseValue(raw, settings) {
        return parseDropdown(raw, settings);
    },
    parseSimpleValue(raw, settings) {
        return parseDropdown(raw, settings);
    },
    toText(value, settings) {
        return renderDropdownText(value, settings);
    },
    toSortKeys(value, settings) {
        if (value === null)
            return { text: null, number: null, date: null };
        return {
            text: textShadow(renderDropdownText(value, settings)),
            // First selected id (**ASSUMED** sort key, open question Q59).
            number: value.ids[0] ?? null,
            date: null,
        };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        // Set comparison: selection order is not semantically meaningful (**ASSUMED**), though the
        // stored order is preserved because `toText` follows it.
        const left = new Set(a.ids);
        const right = new Set(b.ids);
        return left.size === right.size && [...left].every((id) => right.has(id));
    },
    toActivityValue(value) {
        return value === null ? null : { ids: value.ids };
    },
    fromMondayValue(raw, settings) {
        const ids = readIds(raw, settings);
        if (ids === null)
            return null;
        // Import keeps every id, even one whose option was deleted (M1-B5 §10 case 1), **and**
        // every key monday shipped beside `ids` (`changed_at`, `override_all_ids`, ...) — the
        // `fromMondayValue` contract is a lossless round trip, and `status.resolveId` already
        // honours it (T-X2 hook 6).
        const rest = raw !== null && typeof raw === 'object' && !Array.isArray(raw)
            ? raw
            : {};
        return { ...rest, ids };
    },
    toMondayValue(value) {
        // The canonical value **is** monday's value; the keys `fromMondayValue` preserved
        // (`changed_at`, `override_all_ids`) survive the round trip (T-X2 hook 6).
        return value === null ? null : { ...value, ids: value.ids };
    },
    toSimpleValue(value) {
        return value === null ? '' : value.ids.join(',');
    },
    fromMondaySettings(raw) {
        const record = (raw ?? {});
        const labels = Array.isArray(record.labels) ? record.labels : [];
        const normalised = [];
        const deactivated = Array.isArray(record.deactivated_labels)
            ? [...record.deactivated_labels]
            : [];
        for (const entry of labels) {
            if (typeof entry !== 'object' || entry === null)
                continue;
            const label = entry;
            const id = Number(label.id);
            if (!Number.isInteger(id))
                continue;
            // `settings_str` uses `name`, the derived `settings` uses `label`.
            const name = typeof label.name === 'string' ? label.name : String(label.label ?? '');
            normalised.push({ id, name });
            if (label.is_deactivated === true && !deactivated.includes(id))
                deactivated.push(id);
        }
        return dropdownSettingsSchema.parse({ ...record, labels: normalised, deactivated_labels: deactivated });
    },
    toMondaySettings(settings) {
        return {
            labels: settings.labels.map((label) => ({
                id: label.id,
                label: label.name,
                is_deactivated: isDeactivatedOption(settings, label.id),
            })),
        };
    },
    toMondaySettingsStr(settings) {
        return { ...settings, labels: settings.labels.map((label) => ({ id: label.id, name: label.name })) };
    },
    /**
     * **An empty cell still answers every non-nullable field of its fragment.** monday returns
     * the zero values, not an absent key: recording `0261` answers an unset `dependency` and an
     * unset `board_relation` with `{display_value: "", linked_item_ids: [], linked_items: []}`,
     * `0260` answers an unset `tags` with `{tags: []}` and `0263` answers an unset
     * `time_tracking` with `{running: false, started_at: null, duration: 0, history: []}`.
     * Returning `{}` leaves the non-nullable fields `undefined`, and **one** empty cell then
     * nulls the whole `items` response (T-5s, M9 §12.2a F-1).
     *
     * The three fragment fields that are **rows** rather than projections —
     * `linked_items`, `subitems` and `history` — are resolved by `/v2` itself
     * (`apps/api/src/graphql/v2/column-value-fields.ts`): the registry has no database.
     */
    toApiFragment(value, settings) {
        if (value === null)
            return { values: [] };
        return {
            values: value.ids
                .filter((id) => findDropdownLabel(settings, id) !== undefined)
                .map((id) => ({ id: String(id), label: findDropdownLabel(settings, id) })),
        };
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /** Dropdown filters by option **id** `[cvs§4.7]`; a multi-select cell is in several buckets. */
    bucketsOf(value, settings) {
        return (value?.ids ?? []).map((id) => ({
            key: id,
            label: findDropdownLabel(settings, id) ?? `#${String(id)}`,
        }));
    },
    /**
     * A kanban drop (`specs/M4-V2-kanban.md` §6.1). Dropdown is **multi-select**, so a card
     * dragged into an option's lane *gains* that option and keeps the others: the item was in
     * every lane its options name and stays in them.
     */
    valueForBucket(bucketKey, settings, current) {
        if (!/^\d+$/.test(bucketKey))
            return null;
        const id = Number(bucketKey);
        if (findDropdownLabel(settings, id) === undefined)
            return null;
        const existing = current?.ids ?? [];
        return existing.includes(id) ? (current ?? { ids: [id] }) : { ...(current ?? {}), ids: [...existing, id] };
    },
    /** Every option, in `settings.labels` order (measured: not by count). */
    bucketVocabulary(settings) {
        return (settings.labels ?? [])
            .filter((option) => !(settings.deactivated_labels ?? []).includes(option.id))
            .map((option) => ({ key: option.id, label: option.name }));
    },
    /** `text is` compares the joined label text, not the option ids (see `status`). */
    compareAttributes: {
        text: {
            shadow: 'text',
            keys: (value, settings) => ({
                text: value === null ? null : textShadow(renderDropdownText(value, settings)),
                number: null,
                date: null,
            }),
        },
    },
    filterSql(ctx) {
        const cell = ctx.refs?.cell ?? 'cv';
        switch (ctx.operator) {
            case 'is_empty':
                return sqlFragment(`${cell}.item_id IS NULL`);
            case 'is_not_empty':
                return sqlFragment(`${cell}.item_id IS NOT NULL`);
            case 'any_of':
                // Compare values are option **ids** `[cvs§4.7]`; jsonb containment uses the GIN index.
                return sqlFragment(`EXISTS (SELECT 1 FROM jsonb_array_elements_text(${cell}.value->'ids') e` +
                    ` WHERE e = ANY(?::text[]))`, [ctx.compareValues.map((value) => String(value))]);
            case 'not_any_of':
                return sqlFragment(`(${cell}.item_id IS NULL OR NOT EXISTS (SELECT 1 FROM` +
                    ` jsonb_array_elements_text(${cell}.value->'ids') e WHERE e = ANY(?::text[])))`, [ctx.compareValues.map((value) => String(value))]);
            default:
                // `contains` / `doesn't contain` / `starts with`, and `text is` / `text is not`
                // (`compare_attribute: "text"`), all compare the joined label text — the shadow.
                return defaultFilterSql('text', ctx);
        }
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map